/*
 * Person area (audit Phase 3.3). The People list and dashboard tile arrived
 * with the Phase 3.2 design system; this module now also carries the Person
 * Detail modal and everything hanging off it, replacing the last of the
 * thirteen wwwroot/js/Person/*.js files. The grants and membership tabs live
 * beside it in person-claim.ts and person-access.ts.
 *
 * Three legacy handlers went rather than moved, all of them unreachable:
 *   Person.Detail.SetPolicy / GetPolicyDetail  -- no caller in any view or
 *       script, and they addressed #CurrentPolicyId / #PolicyApplyButton /
 *       #PolicyDetailBusyIndicator, none of which exist. Policy assignment is
 *       Person.PolicyAssign below, reached from PersonPolicy/PolicyAssign.cshtml.
 *   Person.Detail.ProfileView -- no caller; Person/ProfileView is rendered
 *       server-side as part of Detail.cshtml.
 *   Person.ForgotPassword -- an empty object, and its whole file.
 *
 * Defects fixed in the move are noted at each handler.
 */
import { ajaxHtml, ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { setAlertMessage } from '../core/alert';
import { all, el, one, swapHtml } from '../core/dom';
import { resetFields, serialize } from '../core/forms';
import { modelStateSummary, type JsonEnvelope } from '../core/http';
import { confirm, hideModal, showModal } from '../core/modal';
import { getTopView } from '../core/nav';
import { toast } from '../core/toast';
import { isValid } from '../core/validation';
import { ListController } from '../components/list';
import {
    currentDomainId, personId, setDisabled, setHidden, summaryOf, value,
} from './person-common';

const list = new ListController({
    form: 'PersonFilterForm',
    container: 'PersonListContainer',
    url: 'Person/Domain',
    busy: '#PersonListBusy',
    alertInto: 'PersonListAlertContainer',
    alertTitle: 'Failed to apply filter',
});
list.bindEnter();

function listDomainId(): number {
    // The footer hidden (written from FilterDomain[0] on render) wins over the
    // rail's #CurrentDomainId: the operator may have filtered to another domain.
    const footer = Number.parseInt(value('MultiActionDomainId'), 10);
    if (Number.isFinite(footer) && footer > 0) return footer;
    const top = Number.parseInt(value('CurrentDomainId'), 10);
    return Number.isFinite(top) ? top : 0;
}

/**
 * Refresh whatever surface the detail modal was opened from. The modal is
 * reachable from the People list, a dashboard tile and the Domain detail
 * page, so "reload the list" is only right when a list is actually behind it.
 */
function refreshBehindModal(): void {
    if (one('PersonListContainer')) void list.apply();
    else void getTopView('#PeopleTopLink', 'Person/Domain');
}

const actionLabels: Record<string, string> = {
    POLICY: 'Assign Policy',
    DEACTIVATE: 'Deactivate',
    REACTIVATE: 'Reactivate',
    PROPERTY: 'Set Property',
    PASSWORDEXP: 'Require Password Change',
    ROLEADD: 'Add Role Membership',
    ROLEDEL: 'Remove Role Membership',
    EMAIL: 'Send Message',
    FIXMEMBERSHIP: 'Fix Domain Membership',
};

function validateActionParams(action: string): string | null {
    switch (action) {
        case 'POLICY':
            if (!value('MultiActionPolicyId')) return 'Choose a Person Policy.';
            break;
        case 'ROLEADD':
        case 'ROLEDEL':
            if (!value('MultiActionRoleId')) return 'Choose a Role.';
            break;
        case 'PROPERTY':
            if (!value('MultiActionClaimType')) return 'Choose a Property.';
            if (!(one('#MultiActionClaimValueWrap #Claim_ClaimValue') as HTMLInputElement | null)?.value) return 'Enter a value for the Property.';
            break;
        case 'EMAIL':
            if (!value('MultiActionEmailSubject')) return 'Enter a subject.';
            if (!value('MultiActionEmailBody')) return 'Enter a message body.';
            break;
        case 'FIXMEMBERSHIP': {
            const mode = (one('#MultiActionForm input[name="FixMode"]:checked') as HTMLInputElement | null)?.value;
            if (!mode) return 'Choose a fix mode.';
            if (mode === 'CHANGE' && !value('MultiActionNewDefaultDomainId')) return 'Choose the new default Domain.';
            break;
        }
        default:
            break;
    }
    return null;
}

interface MultiActionError { personId?: number; userName?: string; message?: string }
interface MultiActionResult { total?: number; succeeded?: number; failed?: number; actionLabel?: string; errors?: MultiActionError[] }

function showFailureModal(result: MultiActionResult): void {
    const body = el('div');
    const summary = el('p');
    summary.append(`${result.succeeded ?? 0} succeeded · `);
    summary.appendChild(el('strong', 'text-danger', `${result.failed ?? 0} failed`));
    summary.append(` · ${result.total ?? 0} total.`);
    const wrap = el('div', 'table-responsive');
    const table = el('table', 'table table-sm table-striped');
    const head = el('thead');
    const headRow = el('tr');
    const who = el('th', undefined, 'Person');
    who.style.width = '35%';
    headRow.append(who, el('th', undefined, 'Reason'));
    head.appendChild(headRow);
    const rows = el('tbody');
    for (const error of result.errors ?? []) {
        const row = el('tr');
        const name = error.userName ? `${error.userName} [#${error.personId ?? '?'}]` : `Person #${error.personId ?? '?'}`;
        row.append(el('td', undefined, name), el('td', undefined, error.message ?? ''));
        rows.appendChild(row);
    }
    table.append(head, rows);
    wrap.appendChild(table);
    body.append(summary, wrap);

    showModal(
        'PersonMultiActionResultModal',
        `${result.actionLabel ?? 'Action'} — Results`,
        body.innerHTML,
        '<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>');
    void list.apply();     // even on partial failure, refresh to reflect the successes
}

function postMultiAction(action: string, ids: number[]): void {
    const form = one('MultiActionForm');
    const data = form ? Array.from(new FormData(form as HTMLFormElement).entries()).map(([name, v]) => ({ name, value: String(v) })) : [];
    data.push({ name: 'ActionCode', value: action });
    ids.forEach((id, i) => data.push({ name: `PersonIdList[${i}]`, value: String(id) }));

    void ajaxJson({
        url: 'Person/MultiAction',
        method: 'POST',
        data,
        onSuccess: (resp: JsonEnvelope) => {
            const result = (resp as { data?: MultiActionResult }).data;
            if (!result) {
                toast('warning', 'Server returned no result body');
                return;
            }
            if ((result.failed ?? 0) === 0) {
                const total = result.total ?? 0;
                toast('success', result.actionLabel ?? 'Action complete', `Applied to ${result.succeeded ?? 0} of ${total} ${total === 1 ? 'person' : 'people'}`);
                void list.apply();
            } else {
                showFailureModal(result);
            }
        },
        onFailure: failure => {
            const modelState = (failure as { modelState?: unknown } | null)?.modelState;
            toast('danger', 'Action failed', (modelState ? modelStateSummary(modelState) : '') || 'Server rejected the request.');
            return true;
        },
        onError: (_xhr, _status, error) => {
            toast('danger', 'Action failed', error);
            return true;
        },
    });
}

export const List = {
    DisplayCreateModal(): void {
        void ajaxModal({
            modal: 'PersonModal',
            url: 'Person/Create',
            formId: 'PersonCreateForm',
            focusId: 'GivenName',
            errorTitle: 'New Person',
            errorMessage: 'New Person View failed to execute:',
        });
    },

    HeaderClick(this: Element): false {
        return list.headerClick(this);
    },

    PagerApply(this: Element): void {
        list.pagerApply(this);
    },

    SelectAll(this: Element): void {
        list.toggleAll(this);
    },

    ShowDetail(person: number | string, target?: string): void {
        const modal = target || 'PersonModal';
        void ajaxModal({
            modal,
            url: `Person/Detail/${person}`,
            busy: '#PersonListBusy',
            errorTitle: `User ${person}`,
            errorMessage: 'User Detail failed to execute:',
            // The first tab opens itself (Detail.cshtml's strip carries
            // data-mcp-autotab), so all that is left here is to point the
            // Policy tab at the operator's current domain, so the assignable
            // -policy list matches the domain being worked in (plus globals),
            // consistent with the bulk "Assign Policy" action.
            onSuccess: () => {
                const policyTab = one('button.nav-link[data-tab="Policy"]');
                const tabUrl = policyTab?.getAttribute('data-tab-url');
                if (policyTab && tabUrl && !tabUrl.includes('domainId=')) {
                    policyTab.setAttribute('data-tab-url', `${tabUrl}${tabUrl.includes('?') ? '&' : '?'}domainId=${value('CurrentDomainId') || 0}`);
                }
            },
        });
    },

    FilterApply(): Promise<void> {
        return list.apply();
    },

    FilterClear(): Promise<void> {
        return list.clear();
    },

    /** Advanced panel's Apply: same path as the toolbar, then close the panel. */
    AdvancedApply(): void {
        const panel = document.getElementById('PersonAdvancedFilter');
        if (panel) window.bootstrap?.Offcanvas.getInstance(panel)?.hide();
        void list.apply();
    },

    /** Advanced panel's Clear: only the advanced inputs; the toolbar fields stay. */
    AdvancedClear(): void {
        resetFields('#PersonAdvancedFilter');
    },

    // ----- bulk actions (footer) -----

    MultiActionChanged(): void {
        const action = value('SelectionAction');
        const container = one('MultiActionParamsContainer');
        if (!action) {
            container?.replaceChildren();
            return;
        }
        void ajaxSwap({
            container: 'MultiActionParamsContainer',
            url: `Person/MultiAction/Params?action=${encodeURIComponent(action)}&domainId=${listDomainId()}`,
            busy: '#MultiActionParamsBusy',
            alertInto: 'PersonListAlertContainer',
            alertTitle: 'Failed to load action parameters',
        });
    },

    /** Cascading Application -> Role picker for ROLEADD / ROLEDEL (appId 0 = global roles). */
    MultiActionRolesLoad(): void {
        const appId = value('MultiActionApplicationId') || '0';
        const select = document.getElementById('MultiActionRoleId') as HTMLSelectElement | null;
        const reset = (): void => {
            if (!select) return;
            select.replaceChildren(new Option('-- Select a Role --', ''));
        };
        void ajaxJson({
            url: `PersonRole/Application/${appId}/Roles`,
            onSuccess: resp => {
                reset();
                const roles = ((resp as { data?: Array<{ id: number; name: string }> }).data) ?? [];
                for (const role of roles) select?.add(new Option(role.name, String(role.id)));
            },
            onFailure: () => { reset(); return true; },
            onError: (_xhr, _status, error) => { toast('danger', 'Error fetching Roles', error); return true; },
        });
    },

    /** Property action: the chosen ClaimType decides the value control. */
    MultiActionClaimTypeChange(): void {
        const typeSelect = document.getElementById('MultiActionClaimType') as HTMLSelectElement | null;
        const selected = typeSelect?.selectedOptions[0];
        const valueType = selected?.getAttribute('data-valuetype') ?? '';
        const valueTypeField = document.getElementById('MultiActionClaimValueType') as HTMLInputElement | null;
        if (valueTypeField) valueTypeField.value = valueType;

        const wrap = one('MultiActionClaimValueWrap');
        if (!wrap) return;
        const plain = el('input', 'form-control');
        plain.type = 'text';
        plain.id = 'Claim_ClaimValue';
        plain.name = 'ClaimValue';
        plain.placeholder = 'Value';
        wrap.replaceChildren(plain);
        if (!valueType) return;

        void ajaxSwap({
            container: '#MultiActionClaimValueWrap > #Claim_ClaimValue',
            url: `PersonClaim/ControlHtml?domainId=${listDomainId()}`,
            method: 'POST',
            data: { Id: 0, PersonId: 0, ClaimType: selected?.value ?? '', ClaimValue: '', ClaimValueType: valueType },
            mode: 'replaceWith',
            alertInto: 'PersonListAlertContainer',
            alertTitle: 'Failed to load value control',
            // A data-autocomp control becomes a datalist through the swap enhancers.
        });
    },

    /** FixMembership mini-form: the new-default-domain picker follows the radio. */
    MultiActionFixModeChange(): void {
        const mode = (one('#MultiActionForm input[name="FixMode"]:checked') as HTMLInputElement | null)?.value;
        const wrap = one('MultiActionFixDomainWrap');
        if (!wrap) return;
        if (mode === 'CHANGE') {
            wrap.classList.remove('d-none');
        } else {
            wrap.classList.add('d-none');
            const picker = document.getElementById('MultiActionNewDefaultDomainId') as HTMLSelectElement | null;
            if (picker) picker.value = '';
        }
    },

    MultiActionApply(): void {
        const action = value('SelectionAction');
        if (!action) {
            toast('warning', 'Pick an action first');
            return;
        }
        const ids = list.selectedIds();
        if (ids.length === 0) {
            toast('warning', 'Select at least one person first');
            return;
        }
        const paramError = validateActionParams(action);
        if (paramError) {
            toast('warning', paramError);
            return;
        }
        const label = actionLabels[action] ?? action;
        const destructive = action === 'DEACTIVATE' || action === 'PASSWORDEXP' || action === 'ROLEDEL';
        void confirm({
            title: `Apply: ${label}`,
            message: `Apply "${label}" to ${ids.length} selected ${ids.length === 1 ? 'person' : 'people'}?`,
            confirmText: 'Apply',
            variant: destructive ? 'danger' : 'primary',
            onConfirm: () => postMultiAction(action, ids),
        });
    },
};

export const Dashboard = {
    /** A dashboard tile: swap the People list (with the tile's filter) into a container. */
    NavigateTitle(url: string, targetId: string): void {
        void ajaxSwap({
            container: targetId,
            url,
            busy: '#PersonDashboardBusyIndicator',
            alertTitle: "Failed to render person's information",
        });
    },
};

// ---------------------------------------------------------------- Create

export const Create = {
    /**
     * "Person must change password" at create time. The old handler was wired
     * to the WRONG checkbox (EmailConfirmed), tested `$(this).checked` -- a
     * jQuery object never has .checked, so the branch was dead -- and would
     * have written the literal text "@DateTime.Now" into the hidden, since a
     * .js file is not a Razor view. The flag therefore never reached the
     * server: PersonCreateModel has no PasswordExpired property, only
     * PasswordExpireDate, and nothing ever set it.
     */
    PasswordExpiredChange(this: Element): void {
        const expired = (this as HTMLInputElement).checked;
        const field = document.getElementById('PasswordExpireDate') as HTMLInputElement | null;
        if (field) field.value = expired ? new Date().toISOString() : '';
    },

    SavePerson(): void {
        const form = one('PersonCreateForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const data = serialize(form);
        data.push({ name: 'DefaultDomainId', value: currentDomainId() });

        setHidden('#PersonCreateContainer .fa-user-plus', true);
        setDisabled('#PersonCreateContainer .fa-save, #PersonCreateSaveButton', true);
        void ajaxSwap({
            container: 'PersonCreateContainer',
            url: 'Person/Create',
            method: 'POST',
            data,
            mode: 'replaceWith',
            busy: '#PersonCreateContainer .fa-spinner',
            // Success renders Detail.cshtml in place of the form (its tab
            // strip opens itself); a rejected model re-renders Create.cshtml
            // with its messages, and the swap enhancers rewire either one.
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to create person', error);
                return true;
            },
            onComplete: () => {
                // replaceWith discarded the original container; re-query.
                setHidden('#PersonCreateContainer .fa-user-plus', false);
                setDisabled('#PersonCreateContainer .fa-save, #PersonCreateSaveButton', false);
            },
        });
    },
};

// ---------------------------------------------------------------- Detail

export const Detail = {
    /**
     * Copy this person into a new create form. The old handler read
     * #PersonIdField, an id that exists nowhere in the solution, so the URL
     * was always Person/Copy/undefined; it then called
     * Person.Create.SetTabClickHandler(), which was never defined, and named
     * a form id ('CreatePersonForm') the view does not use.
     */
    Copy(): void {
        const person = personId();
        if (!person) return;
        hideModal('PersonModal');
        void ajaxModal({
            modal: 'PersonModal',
            url: `Person/Copy/${person}`,
            formId: 'PersonCreateForm',
            focusId: 'GivenName',
            errorTitle: 'Copy Person',
            errorMessage: 'Failed to execute Person Copy:',
        });
    },

    Delete(person?: number | string): void {
        const id = String(person ?? personId());
        const userName = value('UserName');
        if (!id || id === '0') return;
        void confirm({
            title: 'Delete user account',
            message: userName ? `Delete user account "${userName}"?` : 'Delete this user account?',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                const actions = '#PersonDetailFooter .btn';
                setDisabled(actions, true);
                void ajaxJson({
                    url: `Person/${id}`,
                    method: 'DELETE',
                    busy: '#PersonDetailContainer .fa-spinner',
                    alertInto: 'PersonDetailMessageContainer',
                    errorTitle: `The request to delete ${userName || `person ${id}`} failed`,
                    onSuccess: () => {
                        hideModal('PersonModal');
                        toast('success', 'User account deleted');
                        refreshBehindModal();
                    },
                    onComplete: () => setDisabled(actions, false),
                });
            },
        });
    },

    /**
     * Clear the account's password lock. The old handler read #PersonIdField
     * (see Copy) so it always called Person/PasswordUnlock/undefined, and it
     * hand-built its two alerts on raw $.ajax; setAlertMessage renders the
     * same anchored alert into #PasswordLockContainer.
     *
     * It is a POST now -- a state-changing GET no antiforgery token guarded --
     * and the endpoint answers the { success, modelState } envelope instead of
     * a bare boolean, so ajaxJson reports the server's own reason for a refusal
     * rather than the handler inventing one.
     */
    PasswordUnlockClick(): false {
        const person = personId();
        if (!person) return false;
        void confirm({
            title: 'Unlock password',
            message: 'Reset the password lock for this user?',
            confirmText: 'Unlock',
            variant: 'primary',
            onConfirm: () => {
                setDisabled('#PasswordUnlockButton', true);
                void ajaxJson({
                    url: `Person/PasswordUnlock/${person}`,
                    method: 'POST',
                    alertInto: 'PasswordUnlockAlert',
                    errorTitle: 'Failed to unlock password',
                    onSuccess: () => {
                        // The lock is gone, so its warning goes with it; the
                        // alert lands in the slot the panel leaves behind
                        // rather than replacing the whole container (which
                        // would take the Unlock button with it on failure).
                        setAlertMessage('PasswordUnlockAlert', 'success', 'The account was unlocked');
                        one('#PasswordLockContainer .alert-warning')?.classList.add('d-none');
                    },
                    onComplete: () => setDisabled('#PasswordUnlockButton', false),
                });
            },
        });
        return false;
    },

    /**
     * Email a fresh confirmation link to the person's unconfirmed address. No
     * confirm dialog: it only sends the person a link, and the Api refuses a
     * second one within a couple of minutes.
     */
    ResendEmailConfirmationClick(): false {
        const person = personId();
        if (!person) return false;
        setDisabled('#ResendEmailConfirmationButton', true);
        void ajaxJson({
            url: `Person/ResendEmailConfirmation/${person}`,
            method: 'POST',
            alertInto: 'ResendEmailConfirmationAlert',
            errorTitle: 'Could not send the confirmation email',
            onSuccess: () => setAlertMessage('ResendEmailConfirmationAlert', 'success',
                'A new confirmation link was emailed. It is good for 24 hours.'),
            onComplete: () => setDisabled('#ResendEmailConfirmationButton', false),
        });
        return false;
    },
};

// ---------------------------------------------------------------- Edit (profile)

export const Edit = {
    Show(person: number | string, targetId: string, msgTargetId?: string): void {
        setDisabled('#PersonDetailEditButton', true);
        void ajaxSwap({
            container: targetId,
            url: `Person/ProfileEdit/${person}`,
            busy: '#PersonBusyIndicator',
            alertInto: msgTargetId ?? targetId,
            alertTitle: "Failed to render person's edit form",
            onComplete: () => setDisabled('#PersonDetailEditButton', false),
        });
    },

    SaveClick(): void {
        const form = one('ProfileEditForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const person = personId();
        setDisabled('#ProfileEditForm button', true);
        void ajaxJson({
            url: `Person/ProfileEdit/${person}`,
            method: 'POST',
            data: serialize(form),
            busy: '#PersonBusyIndicator',
            alertInto: 'PersonDetailMessageContainer',
            errorTitle: 'An error occurred attempting to save changes',
            onSuccess: () => {
                toast('success', 'Changes saved');
                List.ShowDetail(person, 'PersonModal');
                refreshBehindModal();
            },
            onComplete: () => setDisabled('#ProfileEditForm button', false),
        });
    },
};

// ---------------------------------------------------------------- Admin password reset

export const ResetPassword = {
    Show(person: number | string, targetId: string): void {
        setDisabled('#PersonDetailFooter .btn', true);
        void ajaxSwap({
            container: targetId,
            url: `Person/PasswordResetAdmin/${person}`,
            busy: '#PersonBusyIndicator',
            alertTitle: 'Password Reset dialog failed to execute',
            onSuccess: () => {
                (one('#ResetPasswordForm #Password') as HTMLInputElement | null)?.focus();
                // The detail actions do not apply while the reset form is up;
                // the form carries its own Cancel.
                for (const button of all('#PersonDetailFooter .btn')) button.classList.add('d-none');
            },
            onComplete: () => setDisabled('#PersonDetailFooter .btn', false),
        });
    },

    /**
     * POST the reset. The endpoint answers Detail.cshtml on success and the
     * form again (with its ModelState) on rejection, so the response decides
     * where it goes: the old code put both into #PersonModal, which on a
     * rejected password dropped a bare <form> in place of the whole dialog.
     */
    Post(): void {
        const form = one('ResetPasswordForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const host = form.parentElement ?? one('PersonViewEditContainer');
        setDisabled('#ResetPasswordForm button', true);
        void ajaxHtml({
            url: 'Person/PasswordResetAdmin',
            method: 'POST',
            data: serialize(form),
            busy: '#PersonBusyIndicator',
            alertTitle: 'An error occurred attempting to reset the password',
            onSuccess: html => {
                if (html.includes('PersonDetailContainer')) {
                    swapHtml('PersonModal', html, 'replace');
                    toast('success', 'Password reset');
                } else if (host) {
                    swapHtml(host, html, 'replace');
                }
            },
            onComplete: () => setDisabled('#ResetPasswordForm button', false),
        });
    },

    Cancel(): void {
        List.ShowDetail(personId());
    },
};

// ---------------------------------------------------------------- Forced remediation

export const Remediate = {
    /**
     * The ProfileView "must be fixed" panel: a primary Domain with no matching
     * membership claim. The server ensures membership and sets it primary in
     * one call, then the whole detail modal reloads so the panel clears and
     * the Domains tab reflects the new membership.
     */
    Apply(): void {
        const person = value('RemediatePersonId');
        const domainId = value('RemediateDomainId');
        if (!(Number(person) > 0) || !(Number(domainId) > 0)) {
            toast('warning', 'Select a domain to continue');
            return;
        }
        void ajaxJson({
            url: `PersonDomain/Person/${person}/Remediate`,
            method: 'POST',
            data: { domainId },
            busy: '#RemediateBusy',
            alertInto: 'RemediateMessageContainer',
            errorTitle: 'Failed to fix the account',
            onSuccess: () => {
                toast('success', 'Account fixed — domain membership set');
                List.ShowDetail(person);
            },
        });
    },
};

// ---------------------------------------------------------------- Image tab

const MAX_IMAGE_BYTES = 2048000;

export const Image = {
    /** Preview the picked file and carry its metadata onto the form. */
    FileChanged(this: Element, control?: Element): void {
        const input = (control ?? this) as HTMLInputElement;
        const file = input.files?.[0];
        const targetId = input.dataset['target'];
        if (!file || !targetId) return;
        const preview = document.getElementById(targetId) as HTMLImageElement | null;
        if (preview) {
            const reader = new FileReader();
            reader.onload = (): void => { preview.src = String(reader.result); };
            reader.readAsDataURL(file);
        }
        setImageMetadata(file);
    },

    ShowUploadWindow(): false {
        void ajaxModal({
            modal: 'PersonImageUploadModal',
            url: `Person/ImageUpload/${personId()}`,
            errorTitle: 'Image Upload',
            errorMessage: 'Failed to load the upload form:',
        });
        return false;
    },

    /**
     * Upload the picked image.
     *
     * The old handler called $('#ImageUploadForm').submit() -- a NATIVE form
     * post from inside a Bootstrap modal. POST Person/ImageUpload answers
     * JSON, so the browser navigated away from the console and rendered the
     * raw envelope as the whole page: the feature never worked. It also
     * stamped the timestamp from File.lastModifiedDate, removed from every
     * current browser, so Timestamp posted as "undefined".
     */
    ImageSave(this: Element): false {
        const form = one('ImageUploadForm') as HTMLFormElement | null;
        const targetId = (this as HTMLElement).dataset['target'];
        const fileInput = targetId ? document.getElementById(targetId) as HTMLInputElement | null : null;
        const file = fileInput?.files?.[0];
        if (!form || !file) {
            toast('warning', 'Choose an image file first');
            return false;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            toast('warning', 'File is too big', 'Please select a file less than 2MB.');
            return false;
        }
        setImageMetadata(file);

        const person = value('ImageUploadUserId') || personId();
        setDisabled('#PrimaryUserImageButtonContainer .btn', true);
        void ajaxJson({
            url: `Person/ImageUpload/${person}`,
            method: 'POST',
            data: new FormData(form),
            contentType: false,
            alertInto: 'ImageUploadMessageContainer',
            errorTitle: 'Failed to upload the image',
            onSuccess: () => {
                hideModal('PersonImageUploadModal');
                toast('success', 'Image uploaded');
                void ajaxSwap({
                    container: 'PersonImagesTab',
                    url: `Person/ImagesDetail/${person}`,
                    alertTitle: 'Failed to refresh the image',
                });
            },
            onFailure: data => { toast('danger', 'Failed to upload the image', summaryOf(data)); return true; },
            onComplete: () => setDisabled('#PrimaryUserImageButtonContainer .btn', false),
        });
        return false;
    },
};

function setImageMetadata(file: File): void {
    const set = (id: string, text: string): void => {
        const field = document.getElementById(id) as HTMLInputElement | null;
        if (field) field.value = text;
    };
    set('ImageUploadName', file.name);
    set('ImageUploadFormat', file.type);
    set('ImageUploadTimestamp', new Date(file.lastModified).toISOString());
}

// ---------------------------------------------------------------- Policy tab

/*
 * Named PolicyAssign, not Policy: wwwroot/js/PersonPolicy/*.js owns
 * window.Person.Policy.{List,Create,Edit} -- the Person Policy ADMIN screen,
 * a different area that has not been migrated yet. The two shared a global
 * only because bundle-personpolicy happened to load second and merged rather
 * than replaced.
 *
 * The legacy handlers addressed '.fa-save', '.fa-undo' and '.fa-spinner' with
 * no scope at all, so assigning a policy disabled every save icon in the open
 * modal -- including the ones on other tabs' rows. Everything here is scoped
 * to the policy card.
 */
const POLICY_CARD = '#PersonPolicyTab';

export const PolicyAssign = {
    Changed(): void {
        const policyId = value('PolicyId');
        setDisabled(`${POLICY_CARD} .fa-save`, true);
        void ajaxSwap({
            container: 'PolicyDetailContainer',
            url: `PersonPolicy/${policyId}`,
            busy: `${POLICY_CARD} .fa-spinner`,
            alertInto: 'PersonPolicyMessageContainer',
            alertTitle: 'Failed to display the policy details',
            onComplete: () => setDisabled(`${POLICY_CARD} .fa-save`, false),
        });
    },

    Save(): void {
        const person = personId();
        const policyId = value('PolicyId');
        if (!(Number(person) > 0) || !(Number(policyId) > 0)) {
            toast('danger', 'Invalid person or policy specified - operation canceled');
            return;
        }
        setDisabled(`${POLICY_CARD} .fa-save`, true);
        void ajaxJson({
            url: `PersonPolicy/Person/${person}/Policy/${policyId}`,
            method: 'PUT',
            busy: `${POLICY_CARD} .fa-spinner`,
            onSuccess: () => toast('success', 'Policy assigned'),
            onFailure: data => { toast('danger', 'Failed to assign the policy', summaryOf(data)); return true; },
            onError: (_xhr, _status, error) => { toast('danger', 'Failed to assign the policy', error); return true; },
            onComplete: () => setDisabled(`${POLICY_CARD} .fa-save`, false),
        });
    },
};

// ---------------------------------------------------------------- App Consent tab

/*
 * The administrator fallback for a person's app consent (FEDERATED-APP-TRUST.md, E).
 * The switch flips immediately; a refusal (no Edit on the person or the application)
 * flips it back and says why.
 */
export const AppConsent = {
    Toggle(this: Element, person: number, applicationId: number): void {
        const box = this as HTMLInputElement;
        const enabled = box.checked;
        box.disabled = true;
        void ajaxJson({
            url: `PersonAppConsent/Person/${person}/Application/${applicationId}`,
            method: 'PUT',
            contentType: 'application/json',
            data: { Enabled: enabled },
            onSuccess: () => toast('success', enabled ? 'Application may now manage this profile' : 'Application consent withdrawn'),
            onFailure: data => { box.checked = !enabled; toast('danger', 'Failed to change app consent', summaryOf(data)); return true; },
            onError: (_xhr, _status, error) => { box.checked = !enabled; toast('danger', 'Failed to change app consent', error); return true; },
            onComplete: () => { box.disabled = false; },
        });
    },
};

// ---------------------------------------------------------------- Templates tab

interface FulfillItem {
    ClaimId: string;
    ClaimValue: string;
    ControlHtml: string;
    TemplateItem: Record<string, unknown>;
}

export const Templates = {
    /**
     * Load a template's items, read-only or editable. Both the picker and the
     * Edit button carry data-action with no data-args, so `readOnly` arrives
     * undefined from the picker: selecting a template opens it editable,
     * which is what the Apply/Edit button pair below then reflects.
     */
    LoadTemplate(readOnly?: boolean): void {
        const templateId = value('PersonTemplateId');
        const person = value('PersonTemplatesPersonId');
        const items = one('PersonTemplateItemsContainer');
        if (!templateId || !person) {
            items?.replaceChildren();
            setDisplay('PersonTemplateEditButton', false);
            setDisplay('PersonTemplateApplyButton', false);
            return;
        }
        setDisabled('#PersonTemplateId', true);
        void ajaxSwap({
            container: 'PersonTemplateItemsContainer',
            url: `ClaimTemplate/ItemFulfill/${templateId}?targetId=${person}&readOnly=${readOnly === true}`,
            busy: '#PersonTemplateBusyIndicator',
            alertTitle: 'An error occurred while loading the template',
            onSuccess: () => {
                setDisplay('PersonTemplateEditButton', readOnly === true);
                setDisplay('PersonTemplateApplyButton', readOnly !== true);
            },
            onComplete: () => setDisabled('#PersonTemplateId', false),
        });
    },

    ApplyTemplate(): void {
        const templateId = value('PersonTemplateId');
        const person = value('PersonTemplatesPersonId');
        if (!templateId || !person) return;

        const fulfillItems: FulfillItem[] = [];
        for (const item of all('#PersonTemplateItemsContainer .template-item')) {
            fulfillItems.push({
                ClaimId: (item.querySelector('input.field-claimid') as HTMLInputElement | null)?.value ?? '',
                ClaimValue: (item.querySelector('td.template-item-input input') as HTMLInputElement | null)?.value ?? '',
                ControlHtml: '',
                TemplateItem: {
                    Id: 0,
                    TemplateId: templateId,
                    ClaimType: (item.querySelector('input.field-claimtype') as HTMLInputElement | null)?.value ?? '',
                    ClaimValueType: '*',
                    Name: '*',
                    Required: false,
                    DisplayOrder: 0,
                    ControlHtml: '*',
                },
            });
        }

        setDisabled('#PersonTemplateId', true);
        void ajaxSwap({
            container: 'PersonTemplateItemsContainer',
            url: 'ClaimTemplate/ItemFulfill',
            method: 'POST',
            data: { TemplateId: templateId, TargetId: person, FulfillItems: fulfillItems },
            busy: '#PersonTemplateBusyIndicator',
            alertTitle: 'An error occurred while applying template changes',
            onSuccess: () => {
                setDisplay('PersonTemplateEditButton', true);
                setDisplay('PersonTemplateApplyButton', false);
                toast('success', 'Template applied');
            },
            onComplete: () => setDisabled('#PersonTemplateId', false),
        });
    },
};

function setDisplay(id: string, shown: boolean): void {
    const node = document.getElementById(id);
    if (node) node.style.display = shown ? '' : 'none';
}

// ---------------------------------------------------------------- Security Q+A tab

export const Challenge = {
    /**
     * Native prompt is good enough here: the value is sensitive (a custom
     * modal would need its own focus/escape handling anyway) and it avoids
     * nesting a second Bootstrap dialog inside the Person Detail modal.
     */
    Set(person: number | string, questionId: number | string): void {
        const answer = window.prompt('Enter your security answer (case-insensitive, whitespace-trimmed):');
        if (answer === null) return;                    // Cancel
        if (!answer.trim()) {
            toast('warning', 'An answer is required.');
            return;
        }
        void ajaxJson({
            url: `PersonChallenge/Person/${person}/Question/${questionId}`,
            method: 'POST',
            data: { answerText: answer },
            alertInto: 'PersonDetailMessageContainer',
            errorTitle: 'Failed to save security answer',
            onSuccess: () => {
                markChallengeRow(questionId, true);
                toast('success', 'Security answer saved.');
            },
            onFailure: data => {
                toast('danger', 'Failed to save answer', summaryOf(data));
                return true;
            },
        });
    },

    Clear(person: number | string, questionId: number | string): void {
        void confirm({
            title: 'Clear security answer',
            message: 'Clear this security answer? The question will need to be answered again before the next password reset.',
            confirmText: 'Clear',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `PersonChallenge/Person/${person}/Question/${questionId}`,
                    method: 'DELETE',
                    alertInto: 'PersonDetailMessageContainer',
                    errorTitle: 'Failed to clear security answer',
                    onSuccess: () => {
                        markChallengeRow(questionId, false);
                        toast('success', 'Security answer cleared.');
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to clear answer', summaryOf(data));
                        return true;
                    },
                });
            },
        });
    },
};

/** Reflect the set/cleared state inline rather than reloading the whole tab. */
function markChallengeRow(questionId: number | string, isSet: boolean): void {
    const row = one(`#PersonChallengesTab tr[data-question-id="${questionId}"]`);
    if (!row) return;
    const status = row.querySelector('[data-role="status"]');
    const clear = row.querySelector('[data-role="clear"]');
    const set = row.querySelector('button[data-action="Person.Challenge.Set"]');
    if (status) {
        status.classList.toggle('bg-success', isSet);
        status.classList.toggle('bg-secondary', !isSet);
        status.replaceChildren();
        if (isSet) {
            const icon = el('i', 'fas fa-check me-1');
            status.append(icon, ' Set');
        } else {
            status.textContent = 'Not set';
        }
    }
    clear?.classList.toggle('d-none', !isSet);
    if (set) set.textContent = isSet ? 'Replace' : 'Set';
}
